import { addToRecord, updateRecord } from '@ms/yammer-data-support';
import type { Reaction } from '@ms/yammer-graphql';
import type { Reducer } from 'redux';

import type {
  MessageReactionsLoadAction,
  MessageReactionsLoadFulfilledAction,
  MessageReactionsLoadPendingAction,
} from '../../domains/messageReactions/actions.types';
import { getId } from '../../domains/messageReactions/getId';
import type { Message } from '../messages/types';

import type { MessageReactions, MessagesReactions } from './types';
export type { MessageReactionsLoadAction };

const initialState: MessagesReactions = {};

const initialMessageReactions: MessageReactions = {
  userIds: [],
  reactions: {},
  cursor: null,
  hasNextPage: false,
  status: 'notRequested',
};

const stateWithPendingReactions = (
  state: MessagesReactions,
  { messageId, reaction, isInitialLoad }: MessageReactionsLoadPendingAction['payload']
): MessagesReactions => {
  const bucketId = getId(messageId, reaction);
  const messageReactions = isInitialLoad ? initialMessageReactions : state[bucketId];

  return addToRecord(state, bucketId, { ...messageReactions, status: 'pending' });
};

const stateWithFulfilledReactions = (
  state: MessagesReactions,
  { messageId, reaction, entities, isInitialLoad }: MessageReactionsLoadFulfilledAction['payload']
): MessagesReactions => {
  const bucketId = getId(messageId, reaction);

  const { cursor, hasNextPage, userIds: newUserIds, reactions: newReactions } = entities.messageReactions[messageId];

  const userIds = isInitialLoad ? newUserIds : [...state[bucketId].userIds, ...newUserIds];
  const reactions = isInitialLoad ? newReactions : { ...state[bucketId].reactions, ...newReactions };

  const fulfilledMessageReactions: MessageReactions = {
    userIds: [...new Set(userIds)],
    reactions,
    cursor,
    hasNextPage,
    status: 'fulfilled',
  };

  return addToRecord(state, bucketId, fulfilledMessageReactions);
};

const stateWithRejectedReactions = (
  state: MessagesReactions,
  messageId: Message['id'],
  reaction: Reaction | null
): MessagesReactions => {
  const bucketId = getId(messageId, reaction);

  return updateRecord(state, bucketId, { status: 'rejected' });
};

const reducer: Reducer<MessagesReactions, MessageReactionsLoadAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'MESSAGE_REACTIONS.LOAD_PENDING': {
      return stateWithPendingReactions(state, action.payload);
    }
    case 'MESSAGE_REACTIONS.LOAD_FULFILLED': {
      return stateWithFulfilledReactions(state, action.payload);
    }
    case 'MESSAGE_REACTIONS.LOAD_REJECTED': {
      return stateWithRejectedReactions(state, action.payload.messageId, action.payload.reaction);
    }
    default:
      return state;
  }
};

export default reducer;
